import React, { useEffect, useState, useCallback } from 'react';
import { createApiClient } from '../lib/apiClient';
import { Loader2, Upload, FileSpreadsheet, Trash2, Info, CheckCircle2, ChevronLeft, Plus, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = createApiClient();
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BLUE = '#002FA7';

const TaxImportTab = ({ year, isAdmin, locked }) => {
  const [batches, setBatches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [newVendor, setNewVendor] = useState({ name: '', category_id: '' });
  const [active, setActive] = useState(null); // active batch detail
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(true);
  const [dupRow, setDupRow] = useState(null);
  const [allowlist, setAllowlist] = useState([]);

  const loadLists = useCallback(async () => {
    const [b, c, r, a] = await Promise.all([
      ax.get('/tax/imports'), ax.get('/tax/categories'), ax.get('/tax/vendors'),
      ax.get('/tax/dup-allowlist').catch(() => ({ data: [] })),
    ]);
    setBatches(b.data.batches || []);
    setCategories(c.data.categories || []);
    setVendors(r.data || []);
    setAllowlist(a.data || []);
  }, []);

  const undoAllowlist = async (id) => {
    setAllowlist((xs) => xs.filter((x) => x.id !== id));
    try { await ax.delete(`/tax/dup-allowlist/${id}`); } catch (e) { loadLists(); }
  };

  useEffect(() => { loadLists(); }, [loadLists]);

  const addVendor = async () => {
    if (!newVendor.name.trim()) return;
    await ax.post('/tax/vendors', { name: newVendor.name.trim(), category_id: newVendor.category_id || null });
    setNewVendor({ name: '', category_id: '' });
    loadLists();
  };
  const setVendorCategory = async (v, category_id) => {
    setVendors((vs) => vs.map((x) => x.id === v.id ? { ...x, category_id } : x));
    await ax.patch(`/tax/vendors/${v.id}`, { name: v.name, category_id: category_id || null });
    loadLists();
  };
  const deleteVendor = async (id) => {
    await ax.delete(`/tax/vendors/${id}`);
    setVendors((vs) => vs.filter((v) => v.id !== id));
  };

  const openBatch = async (id) => {
    const r = await ax.get(`/tax/imports/${id}`);
    setActive(r.data);
  };

  const upload = async (files) => {
    const file = files?.[0]; if (!file) return;
    setUploading(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('year', year);
      const r = await ax.post('/tax/imports', fd);
      setActive(r.data);
      loadLists();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Upload failed.'); setMsgOk(false);
    } finally { setUploading(false); }
  };

  const patchRow = async (rid, patch) => {
    setActive((a) => ({ ...a, rows: a.rows.map((r) => r.id === rid ? { ...r, ...patch } : r) }));
    try { await ax.patch(`/tax/imports/${active.id}/rows/${rid}`, patch); } catch (e) {}
  };

  const post = async () => {
    setPosting(true); setMsg('');
    try {
      const r = await ax.post(`/tax/imports/${active.id}/post`, { learn_rules: true });
      setMsg(`Posted ${r.data.posted} expense(s)${r.data.rules_learned ? `, learned ${r.data.rules_learned} vendor rule(s)` : ''}.`); setMsgOk(true);
      await openBatch(active.id);
      loadLists();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Post failed.'); setMsgOk(false);
    } finally { setPosting(false); }
  };

  const deleteBatch = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this import? Posted expenses are kept; staged rows are removed.')) return;
    await ax.delete(`/tax/imports/${id}`);
    loadLists();
  };

  // ---- Review table for an open batch ----
  if (active) {
    const includable = active.rows.filter((r) => r.status !== 'posted');
    const selectedCount = includable.filter((r) => r.include).length;
    return (
      <div data-testid="tax-import-detail" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <button onClick={() => setActive(null)} data-testid="import-back" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-3"><ChevronLeft className="w-4 h-4" /> All imports</button>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{active.filename}</h3>
            <p className="text-xs text-slate-500">{active.row_count} rows · {active.posted_count} posted · status {active.status}</p>
          </div>
          {isAdmin && !locked && selectedCount > 0 && (
            <button data-testid="import-post-btn" onClick={post} disabled={posting}
              className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Post {selectedCount} Selected
            </button>
          )}
        </div>
        {msg && <div data-testid="import-msg" className={`mb-3 px-4 py-2.5 rounded-md text-sm border ${msgOk ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>{msg}</div>}
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left">Post</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Vendor</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {active.rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-50" data-testid={`import-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" data-testid={`import-include-${r.id}`} disabled={r.status === 'posted'} checked={!!r.include}
                      onChange={(e) => patchRow(r.id, { include: e.target.checked })} />
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.expense_date}</td>
                  <td className="px-3 py-2 text-slate-900 font-medium">{r.vendor_name || '—'}
                    {r.is_duplicate && <button type="button" data-testid={`import-dup-${r.id}`} onClick={() => setDupRow(r)} title="Matches an expense already recorded — click to compare" className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 hover:bg-red-200">Possible duplicate</button>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-900">{money(r.amount)}</td>
                  <td className="px-3 py-2">
                    <select data-testid={`import-cat-${r.id}`} disabled={r.status === 'posted'} value={r.category_id || ''}
                      onChange={(e) => patchRow(r.id, { category_id: e.target.value })}
                      className="border border-slate-300 rounded px-2 py-1 text-xs max-w-[180px]">
                      <option value="">Uncategorized</option>
                      {categories.filter((c) => c.enabled !== false).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {r.status === 'posted'
                      ? <span className="text-green-600 text-xs font-semibold">Posted</span>
                      : <span className="text-slate-400 text-xs">Staged</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {dupRow && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="dup-compare-modal" onClick={() => setDupRow(null)}>
            <div className="bg-white rounded-lg w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Possible duplicate</h3>
                <button data-testid="dup-compare-close" onClick={() => setDupRow(null)} className="text-slate-400 hover:text-slate-700">✕</button>
              </div>
              <div className="p-5 grid grid-cols-2 gap-4 text-sm">
                <div className="border border-red-200 bg-red-50 rounded-md p-3">
                  <p className="text-[11px] font-bold uppercase text-red-600 mb-2">This import row</p>
                  <p className="font-semibold text-slate-900">{dupRow.vendor_name || '—'}</p>
                  <p className="text-slate-600">{money(dupRow.amount)}</p>
                  <p className="text-slate-500 text-xs">{dupRow.expense_date}</p>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[11px] font-bold uppercase text-slate-500 mb-2">Already recorded</p>
                  {dupRow.duplicate_of ? (
                    <>
                      <p className="font-semibold text-slate-900">{dupRow.duplicate_of.vendor_name || '—'}</p>
                      <p className="text-slate-600">{money(dupRow.duplicate_of.amount)}</p>
                      <p className="text-slate-500 text-xs">{dupRow.duplicate_of.expense_date}</p>
                      <p className="text-slate-500 text-xs mt-1">{dupRow.duplicate_of.category_name || 'Uncategorized'}</p>
                    </>
                  ) : <p className="text-slate-500 text-xs">Also appears earlier in this same file.</p>}
                </div>
              </div>
              <div className="px-5 pb-4 flex justify-end gap-2">
                <button data-testid="dup-keep-excluded" onClick={() => setDupRow(null)} className="text-sm font-semibold text-slate-600 border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50">Keep excluded</button>
                <button data-testid="dup-import-anyway" onClick={() => { patchRow(dupRow.id, { include: true }); setDupRow(null); }} className="text-sm font-semibold text-white bg-[#002FA7] rounded-md px-3 py-1.5 hover:bg-[#00247D]">Import anyway</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Batch list + upload + rules ----
  return (
    <div data-testid="tax-import-tab" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-4">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        Upload a fuel-card or bank statement (CSV or Excel .xlsx). We detect the columns, guess categories from what you've imported before, and let you review every row before posting.
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Import Center</h3>
        {isAdmin && !locked && (
          <label data-testid="import-upload" className={`flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold cursor-pointer hover:bg-[#00247D] ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload CSV / Excel
            <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => upload(e.target.files)} />
          </label>
        )}
      </div>
      {msg && <div data-testid="import-list-msg" className={`mb-3 px-4 py-2.5 rounded-md text-sm border ${msgOk ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>{msg}</div>}

      {batches.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-400">No imports yet. Upload a CSV to get started.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6">
          {batches.map((b) => (
            <div key={b.id} data-testid={`import-batch-${b.id}`} className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-50 hover:bg-slate-50">
              <button onClick={() => openBatch(b.id)} className="flex items-center gap-2 text-left flex-1">
                <FileSpreadsheet className="w-4 h-4" style={{ color: BLUE }} />
                <div>
                  <p className="text-sm font-semibold text-slate-800">{b.filename}</p>
                  <p className="text-xs text-slate-400">{b.year} · {b.row_count} rows · {b.posted_count} posted</p>
                </div>
              </button>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${b.status === 'posted' ? 'bg-green-50 text-green-700 border-green-200' : b.status === 'partial' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{b.status}</span>
                {isAdmin && <button data-testid={`import-delete-${b.id}`} onClick={(e) => deleteBatch(b.id, e)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Vendors Master */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="vendors-master">
        <h4 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>Vendors</h4>
        <p className="text-xs text-slate-400 mb-3">A clean list of vendors and their default category. Used to auto-fill categories on imports and manual entry. Imports add new vendors automatically.</p>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <input data-testid="vendor-new-name" value={newVendor.name} onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
              placeholder="Vendor name (e.g. Pilot Flying J)" className="border border-slate-300 rounded px-3 py-1.5 text-sm flex-1 min-w-[180px]" />
            <select data-testid="vendor-new-category" value={newVendor.category_id} onChange={(e) => setNewVendor({ ...newVendor, category_id: e.target.value })}
              className="border border-slate-300 rounded px-2 py-1.5 text-sm max-w-[200px]">
              <option value="">Default category…</option>
              {categories.filter((c) => c.enabled !== false).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="vendor-add-btn" onClick={addVendor} disabled={!newVendor.name.trim()}
              className="flex items-center gap-1 bg-[#002FA7] text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-[#00247D] disabled:opacity-40"><Plus className="w-4 h-4" /> Add</button>
          </div>
        )}
        {vendors.length === 0 ? (
          <p className="text-xs text-slate-400">No vendors yet. Add one above or post an import to learn them.</p>
        ) : (
          <div className="divide-y divide-slate-50 border border-slate-100 rounded-md max-h-72 overflow-y-auto">
            {vendors.map((v) => (
              <div key={v.id} data-testid={`vendor-${v.id}`} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="font-medium text-slate-800">{v.name}</span>
                <div className="flex items-center gap-2">
                  {isAdmin ? (
                    <select data-testid={`vendor-cat-${v.id}`} value={v.category_id || ''} onChange={(e) => setVendorCategory(v, e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1 text-xs max-w-[180px]">
                      <option value="">Uncategorized</option>
                      {categories.filter((c) => c.enabled !== false).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  ) : <span className="text-xs text-slate-500">{v.category_name || 'Uncategorized'}</span>}
                  {isAdmin && <button data-testid={`vendor-delete-${v.id}`} onClick={() => deleteVendor(v.id)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Duplicate allowlist manager — undo "not a duplicate" pairs */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mt-6" data-testid="allowlist-manager">
        <h4 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>Duplicate Allowlist</h4>
        <p className="text-xs text-slate-400 mb-3">Expenses you've marked "not a duplicate" so they stop being flagged. Undo any pair to let duplicate detection catch it again.</p>
        {allowlist.length === 0 ? (
          <p className="text-xs text-slate-400">Nothing on the allowlist. When you import a possible duplicate anyway, it lands here.</p>
        ) : (
          <div className="divide-y divide-slate-50 border border-slate-100 rounded-md max-h-72 overflow-y-auto">
            {allowlist.map((a) => (
              <div key={a.id} data-testid={`allowlist-${a.id}`} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="min-w-0 truncate">
                  <span className="font-medium text-slate-800">{a.vendor || 'Unknown vendor'}</span>
                  <span className="text-slate-500"> · {money(a.amount)}</span>
                  {a.expense_date && <span className="text-slate-400 text-xs"> · {a.expense_date}</span>}
                </span>
                {isAdmin && (
                  <button data-testid={`allowlist-undo-${a.id}`} onClick={() => undoAllowlist(a.id)} title="Undo — flag as duplicate again"
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 border border-slate-200 rounded px-2 py-1 hover:border-[#002FA7] hover:text-[#002FA7] shrink-0">
                    <RotateCcw className="w-3.5 h-3.5" /> Undo
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxImportTab;
